import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import AuthTextField from './AuthTextField';
import { authCardStyle, authButtonStyle } from '../constants/authStyles';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { registerNativePushFromSession } from '../utils/mobilePushBridge';
import {
  acquireOAuthIdToken,
  consumeAppleRedirectResult,
  consumeGoogleRedirectIdToken,
  isAppleOAuthConfigured,
  isGoogleOAuthConfigured,
  isOAuthAvailable,
  preloadAppleSignIn,
  preloadGoogleSignIn,
  renderGoogleButton,
} from '../utils/oauthSignIn';
import { isText2MobileApp } from '../utils/mobileOAuthBridge';

// Giriş sağlayıcılarının resmi logoları (düğmenin kendisi oyunun tasarımında).
const GoogleLogo = () => (
  <svg className="auth-oauth-logo" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const AppleLogo = () => (
  <svg className="auth-oauth-logo auth-oauth-logo--apple" viewBox="0 0 384 512" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const Login = ({ setIsAuthenticated }) => {
  const { t, i18n } = useTranslation('selection');
  const { t: tc } = useTranslation('common');
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [oauthUsername, setOauthUsername] = useState('');
  const [oauthPending, setOauthPending] = useState(null);
  const { setError, clearError } = usePopupMessage();
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState('');
  const navigate = useNavigate();

  const showGoogleButton = isText2MobileApp() || isGoogleOAuthConfigured();
  const showAppleButton = isText2MobileApp() || isAppleOAuthConfigured();
  const showOAuthSection = isOAuthAvailable() && (showGoogleButton || showAppleButton);

  useEffect(() => {
    const username = localStorage.getItem('username');
    const token = localStorage.getItem('token');
    if (username && token) {
      setIsAuthenticated?.(true);
      registerNativePushFromSession();
      navigate('/game', { replace: true });
    }
  }, [navigate, setIsAuthenticated]);

  useEffect(() => {
    preloadGoogleSignIn();
    preloadAppleSignIn();
  }, []);

  // Web'de Google'ın resmi düğmesi (pencere ile giriş); yüklenemezse eski düğme kalır.
  const googleBtnRef = useRef(null);
  const googleCredentialRef = useRef(null);
  const [googleRendered, setGoogleRendered] = useState(false);
  const useGoogleGsiButton = !isText2MobileApp() && isGoogleOAuthConfigured();
  useEffect(() => {
    if (!useGoogleGsiButton || !googleBtnRef.current) return undefined;
    let alive = true;
    renderGoogleButton(
      googleBtnRef.current,
      (credential) => googleCredentialRef.current?.(credential),
      (i18n.language || 'tr').slice(0, 2),
    ).then((ok) => {
      if (alive) setGoogleRendered(ok);
    });
    return () => {
      alive = false;
    };
  }, [useGoogleGsiButton, i18n.language]);

  const completeSession = (token, username) => {
    localStorage.setItem('token', token);
    localStorage.setItem('username', username);
    setIsAuthenticated(true);
    registerNativePushFromSession();
    navigate('/game');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    clearError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setLoading(true);

    try {
      const response = await axios.post('/api/login', formData);
      
      if (response.data.success) {
        completeSession(response.data.token, formData.username);
      } else {
        setError(response.data.message || t('loginFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || tc('errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthResponse = (response) => {
    if (!response.data.success) {
      setError(response.data.message || t('oauthFailed'));
      return;
    }

    if (response.data.needsUsername) {
      setOauthPending({
        pendingToken: response.data.pendingToken,
        email: response.data.email || '',
      });
      setOauthUsername('');
      clearError();
      return;
    }

    const username = response.data.username;
    if (!response.data.token || !username) {
      setError(t('oauthFailed'));
      return;
    }

    completeSession(response.data.token, username);
  };

  useEffect(() => {
    let cancelled = false;

    const resumeOAuthRedirect = async () => {
      let provider = '';
      let idToken;
      try {
        idToken = consumeGoogleRedirectIdToken();
        if (idToken) {
          provider = 'google';
        } else {
          idToken = consumeAppleRedirectResult();
          if (idToken) {
            provider = 'apple';
          }
        }
      } catch (err) {
        const message = err?.message || t('oauthFailed');
        if (!String(message).toLowerCase().includes('cancel')) {
          setError(message);
        }
        return;
      }

      if (!idToken || !provider || cancelled) {
        return;
      }

      setOauthLoading(provider);
      try {
        const response = await axios.post('/api/auth/oauth', { provider, idToken });
        if (!cancelled) {
          handleOAuthResponse(response);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || t('oauthFailed'));
        }
      } finally {
        if (!cancelled) {
          setOauthLoading('');
        }
      }
    };

    resumeOAuthRedirect();
    return () => {
      cancelled = true;
    };
  }, [setError, t]);

  const submitOAuthIdToken = async (provider, idToken) => {
    clearError();
    setOauthLoading(provider);
    try {
      const response = await axios.post('/api/auth/oauth', { provider, idToken });
      handleOAuthResponse(response);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('oauthFailed'));
    } finally {
      setOauthLoading('');
    }
  };
  googleCredentialRef.current = (credential) => submitOAuthIdToken('google', credential);

  const handleOAuthSignIn = async (provider) => {
    clearError();
    setOauthLoading(provider);

    try {
      const idToken = await acquireOAuthIdToken(provider);
      const response = await axios.post('/api/auth/oauth', { provider, idToken });
      handleOAuthResponse(response);
    } catch (err) {
      const message = err.response?.data?.message || err.message || t('oauthFailed');
      if (!String(message).toLowerCase().includes('cancel')) {
        setError(message);
      }
    } finally {
      setOauthLoading('');
    }
  };

  const handleOAuthComplete = async (e) => {
    e.preventDefault();
    if (!oauthPending?.pendingToken) {
      return;
    }

    clearError();
    setOauthLoading('complete');

    try {
      const response = await axios.post('/api/auth/oauth/complete', {
        pendingToken: oauthPending.pendingToken,
        username: oauthUsername.trim(),
      });
      handleOAuthResponse(response);
      if (response.data.success && !response.data.needsUsername) {
        setOauthPending(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || tc('errorGeneric'));
    } finally {
      setOauthLoading('');
    }
  };

  if (oauthPending) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={authCardStyle}>
          <div className="auth-header">
            <h1>{t('oauthUsernameTitle')}</h1>
            <p>{t('oauthUsernameSubtitle')}</p>
          </div>

          {oauthPending.email ? (
            <p className="auth-oauth-email">{oauthPending.email}</p>
          ) : null}

          <form onSubmit={handleOAuthComplete} className="auth-form">
            <div className="form-group">
              <label htmlFor="oauth-username">{t('username')}</label>
              <AuthTextField
                id="oauth-username"
                name="oauth-username"
                type="text"
                value={oauthUsername}
                onChange={(e) => {
                  setOauthUsername(e.target.value);
                  clearError();
                }}
                required
                placeholder={t('usernamePlaceholder')}
                autoComplete="username"
              />
            </div>

            <button
              type="submit"
              className="auth-button"
              style={authButtonStyle}
              disabled={oauthLoading === 'complete'}
            >
              {oauthLoading === 'complete' ? t('oauthUsernameSubmitting') : t('oauthUsernameSubmit')}
            </button>
          </form>

          <div className="auth-footer">
            <button
              type="button"
              className="auth-link-button"
              onClick={() => {
                setOauthPending(null);
                setOauthUsername('');
                clearError();
              }}
            >
              {t('oauthBackToLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card" style={authCardStyle}>
        <div className="auth-header">
          <h1>{t('loginTitle')}</h1>
          <p>{t('loginSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="username">{t('username')}</label>
            <AuthTextField
              id="username"
              name="username"
              type="text"
              value={formData.username}
              onChange={handleChange}
              required
              placeholder={t('usernamePlaceholder')}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('password')}</label>
            <AuthTextField
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder={t('passwordPlaceholder')}
              autoComplete="current-password"
            />
          </div>

          <button 
            type="submit" 
            className="auth-button"
            style={authButtonStyle}
            disabled={loading}
          >
            {loading ? t('loginSubmitting') : t('loginSubmit')}
          </button>
        </form>

        {showOAuthSection ? (
          <div className="auth-oauth-section">
            <div className="auth-oauth-divider">
              <span>{t('oauthDivider')}</span>
            </div>
            <div className="auth-oauth-buttons">
              {showGoogleButton ? (
                <div className="auth-oauth-slot">
                  <button
                    type="button"
                    className="auth-button auth-oauth-button auth-oauth-button--google"
                    style={authButtonStyle}
                    onClick={() => handleOAuthSignIn('google')}
                    disabled={!!oauthLoading || loading}
                    tabIndex={googleRendered ? -1 : 0}
                  >
                    <GoogleLogo />
                    <span>{oauthLoading === 'google' ? t('oauthSigningIn') : t('oauthGoogle')}</span>
                  </button>
                  {useGoogleGsiButton ? (
                    // Google'ın resmi düğmesi oyun düğmesinin üstünde görünmez durur; tıklama
                    // pencereyle girişi başlatır, görünüm oyunun düğme tasarımında kalır.
                    <div
                      ref={googleBtnRef}
                      className="auth-google-gsi"
                      style={googleRendered && !oauthLoading ? undefined : { display: 'none' }}
                      aria-hidden="true"
                    />
                  ) : null}
                </div>
              ) : null}
              {showAppleButton ? (
                <button
                  type="button"
                  className="auth-button auth-oauth-button auth-oauth-button--apple"
                  style={authButtonStyle}
                  onClick={() => handleOAuthSignIn('apple')}
                  disabled={!!oauthLoading || loading}
                >
                  <AppleLogo />
                  <span>{oauthLoading === 'apple' ? t('oauthSigningIn') : t('oauthApple')}</span>
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="auth-footer">
          <p>
            {t('noAccount')}{' '}
            <Link to="/register" className="auth-link">
              {t('registerLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
